import React, { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './HorseRacingIntegration.css';

const HorseRacingIntegration = () => {
  const { user } = useAuth();

  // Date selector (defaults to today)
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr);

  // Tab: 'racecards' | 'results'
  const [activeTab, setActiveTab] = useState('racecards');

  const [racecards, setRacecards] = useState([]);
  const [resultsData, setResultsData] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);

  const [loadingRacecards, setLoadingRacecards] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState({});

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // -----------------------------------------------------------------------
  // Data fetchers
  // -----------------------------------------------------------------------

  const fetchRacecards = useCallback(async () => {
    setLoadingRacecards(true);
    setError('');
    try {
      const res = await api.get(`/horse-racing/racecards?date=${date}`);
      setRacecards(res.data.racecards || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch racecards');
    } finally {
      setLoadingRacecards(false);
    }
  }, [date]);

  const fetchResults = useCallback(async () => {
    setLoadingResults(true);
    setError('');
    try {
      const res = await api.get(`/horse-racing/results?date=${date}`);
      setResultsData(res.data.results || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch results');
    } finally {
      setLoadingResults(false);
    }
  }, [date]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await api.get('/horse-racing/sync-status');
      setSyncStatus(res.data);
    } catch (err) {
      // Non-critical
      console.error('Could not load sync status', err);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Admin actions
  // -----------------------------------------------------------------------

  const handleSync = async (force = false) => {
    setSyncing(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/horse-racing/sync', { date, force });
      const d = res.data;
      setSuccess(d.message || 'Sync complete');
      fetchSyncStatus();
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleImport = async (raceId) => {
    setImporting((prev) => ({ ...prev, [raceId]: true }));
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/horse-racing/import', { id: raceId });
      setSuccess(res.data.message || `Imported race ${raceId}`);
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to import race ${raceId}`);
    } finally {
      setImporting((prev) => ({ ...prev, [raceId]: false }));
    }
  };

  // -----------------------------------------------------------------------
  // Date change — clear stale data
  // -----------------------------------------------------------------------
  const handleDateChange = (e) => {
    setDate(e.target.value);
    setRacecards([]);
    setResultsData([]);
    setError('');
  };

  // -----------------------------------------------------------------------
  // Auth guard
  // -----------------------------------------------------------------------
  if (!user || user.role !== 'admin') {
    return (
      <div className="horse-racing-integration">
        <p>Admin access required</p>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  const renderSyncStatus = () => {
    if (!syncStatus) {
      return (
        <button onClick={fetchSyncStatus} className="btn-secondary btn-sm">
          Check Sync Status
        </button>
      );
    }
    return (
      <div className="sync-status-box">
        <span className={`sync-allowed ${syncStatus.syncAllowed ? 'yes' : 'no'}`}>
          {syncStatus.syncAllowed ? '✓ Sync available' : '⏳ Cooldown active'}
        </span>
        {syncStatus.lastSyncAt && (
          <span className="last-sync">
            Last sync: {new Date(syncStatus.lastSyncAt).toLocaleString()}
          </span>
        )}
        <span className="sync-reason">{syncStatus.message}</span>
      </div>
    );
  };

  const renderRacecard = (racecard, idx) => {
    const raceId = racecard.id || racecard.race_id || idx;
    const runners = racecard.runners || racecard.horses || [];
    return (
      <div key={raceId} className="series-group">
        <div className="series-header">
          <div className="series-info">
            <h4>{racecard.title || racecard.race_name || `Race ${idx + 1}`}</h4>
            <span className="series-ticker">{racecard.course || racecard.venue}</span>
            {racecard.off_time || racecard.time ? (
              <span className="close-time">Off: {racecard.off_time || racecard.time}</span>
            ) : null}
            {racecard.distance && (
              <span className="market-count">Distance: {racecard.distance}</span>
            )}
            {racecard.going && (
              <span className="market-count">Going: {racecard.going}</span>
            )}
            <span className="market-count">{runners.length} runners</span>
          </div>
          {raceId && (
            <button
              onClick={() => handleImport(String(raceId))}
              disabled={!!importing[String(raceId)] || syncing}
              className="btn-import"
            >
              {importing[String(raceId)] ? 'Importing…' : 'Import to DB'}
            </button>
          )}
        </div>

        {runners.length > 0 && (
          <div className="markets-list">
            {runners.map((runner, ri) => {
              const name = runner.horse || runner.name || runner.horseName || '—';
              const no = runner.number || runner.no || runner.cloth || (ri + 1);
              const odds = runner.odds || runner.sp || '—';
              return (
                <div key={ri} className="market-card">
                  <div className="market-title">
                    #{no} {name}
                    {runner.jockey ? <span className="market-ticker"> J: {runner.jockey}</span> : null}
                    {runner.trainer ? <span className="market-ticker"> T: {runner.trainer}</span> : null}
                  </div>
                  <div className="market-details">
                    {odds !== '—' && <span className="market-volume">Odds: {odds}</span>}
                    {runner.form && <span className="market-ticker">Form: {runner.form}</span>}
                    {runner.weight && <span className="market-volume">{runner.weight}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderResult = (result, idx) => {
    const runnersList = result.runners || result.horses || [];
    return (
      <div key={result.id || idx} className="series-group">
        <div className="series-header">
          <div className="series-info">
            <h4>{result.title || result.race_name || `Race ${idx + 1}`}</h4>
            <span className="series-ticker">{result.course || result.venue}</span>
            {result.off_time || result.time ? (
              <span className="close-time">Off: {result.off_time || result.time}</span>
            ) : null}
          </div>
        </div>
        {runnersList.length > 0 && (
          <div className="markets-list">
            {runnersList.map((runner, ri) => {
              const name = runner.horse || runner.name || '—';
              const pos = runner.position || runner.pos || (ri + 1);
              const sp = runner.sp || runner.starting_price || '—';
              return (
                <div key={ri} className="market-card">
                  <div className="market-title">
                    <strong>{pos}.</strong> {name}
                    {runner.jockey ? <span className="market-ticker"> J: {runner.jockey}</span> : null}
                  </div>
                  <div className="market-details">
                    {sp !== '—' && <span className="market-volume">SP: {sp}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------
  return (
    <div className="horse-racing-integration">
      <div className="horse-racing-header">
        <h2>Horse Racing API</h2>
        <p className="subtitle">
          UK &amp; Ireland races via RapidAPI — manual fetch only (rate-limited)
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <strong>Success:</strong> {success}
        </div>
      )}

      {/* Controls */}
      <div className="horse-racing-controls">
        <div className="filter-controls">
          <label>
            Date:&nbsp;
            <input
              type="date"
              value={date}
              onChange={handleDateChange}
              max={todayStr}
            />
          </label>

          <div className="tab-buttons">
            <button
              className={`btn-secondary ${activeTab === 'racecards' ? 'active' : ''}`}
              onClick={() => setActiveTab('racecards')}
            >
              Racecards
            </button>
            <button
              className={`btn-secondary ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              Results
            </button>
          </div>

          {activeTab === 'racecards' ? (
            <button
              onClick={fetchRacecards}
              disabled={loadingRacecards}
              className="btn-secondary"
            >
              {loadingRacecards ? 'Loading…' : 'Fetch Racecards'}
            </button>
          ) : (
            <button
              onClick={fetchResults}
              disabled={loadingResults}
              className="btn-secondary"
            >
              {loadingResults ? 'Loading…' : 'Fetch Results'}
            </button>
          )}
        </div>

        {/* Sync controls (admin) */}
        <div className="sync-controls">
          {renderSyncStatus()}
          <button
            onClick={() => handleSync(false)}
            disabled={syncing || loadingRacecards}
            className="btn-primary btn-sync"
          >
            {syncing ? 'Syncing…' : `Sync ${date} to Database`}
          </button>
          <button
            onClick={() => handleSync(true)}
            disabled={syncing}
            className="btn-secondary"
            title="Skip the 1-hour cooldown (use sparingly)"
          >
            Force Sync
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="markets-container">
        {activeTab === 'racecards' ? (
          <>
            <h3>Racecards — {date} ({racecards.length} races)</h3>
            {racecards.length === 0 ? (
              <p className="no-markets">
                No racecards loaded. Choose a date and click "Fetch Racecards".
              </p>
            ) : (
              <div className="series-groups">
                {racecards.map((rc, i) => renderRacecard(rc, i))}
              </div>
            )}
          </>
        ) : (
          <>
            <h3>Results — {date} ({resultsData.length} races)</h3>
            {resultsData.length === 0 ? (
              <p className="no-markets">
                No results loaded. Choose a date and click "Fetch Results".
              </p>
            ) : (
              <div className="series-groups">
                {resultsData.map((r, i) => renderResult(r, i))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="horse-racing-info">
        <h3>About the Horse Racing API</h3>
        <ul>
          <li><strong>Source:</strong> RapidAPI — UK &amp; Ireland races (ortegalex/horse-racing)</li>
          <li><strong>Racecards:</strong> View today's or any past race entries including horse, jockey, trainer, and odds</li>
          <li><strong>Results:</strong> View finishing positions and starting prices</li>
          <li><strong>Import to DB:</strong> Save a single race from the live API into the database</li>
          <li><strong>Sync to Database:</strong> Sync all racecards for the selected date — limited to once per hour to conserve API quota</li>
          <li><strong>Force Sync:</strong> Override the cooldown — use sparingly to avoid hitting daily API limits</li>
        </ul>
      </div>
    </div>
  );
};

export default HorseRacingIntegration;
